import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { apiGet, clearImpersonationToken, getImpersonationToken } from '../lib/api'

const AUTH_FAILURE_STATUSES = new Set([401, 403])
const PATIENT_PROFILE_CACHE_MS = 25 * 1000
const PATIENT_PROFILE_LOAD_TIMEOUT_MS = 14000
let cachedPatientProfile = null
let cachedPatientProfileKey = ''
let cachedPatientProfileExpiresAt = 0
let patientProfilePromise = null
let patientProfilePromiseKey = ''

function isAuthFailure(err) {
  return AUTH_FAILURE_STATUSES.has(Number(err?.status))
}

function isSourceUnavailable(err) {
  const status = Number(err?.status || 0)
  const message = String(err?.message || '')
  return status === 408 || status === 503 || /timeout|timed out|temporarily unavailable|source unavailable|network|fetch/i.test(message)
}

function patientProfileCacheKey(session, impersonationToken) {
  if (impersonationToken) return `impersonation:${impersonationToken.length}:${impersonationToken.slice(0, 24)}`
  return `session:${session?.user?.id || ''}:${String(session?.access_token || '').slice(-24)}`
}

function clearPatientProfileCache() {
  cachedPatientProfile = null
  cachedPatientProfileKey = ''
  cachedPatientProfileExpiresAt = 0
  patientProfilePromise = null
  patientProfilePromiseKey = ''
}

function withTimeout(promise, ms, message) {
  let timeoutId = null
  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => {
      const err = new Error(message)
      err.status = 408
      reject(err)
    }, ms)
  })
  return Promise.race([promise, timeout]).finally(() => {
    if (timeoutId) window.clearTimeout(timeoutId)
  })
}

function msoProfileState(me, session, impersonationToken) {
  const p = me.patient
  return {
    loading: false,
    patient: {
      ...p,
      full_name: [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email,
      fullName: [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email,
      mso: true,
    },
    org: null,
    session: impersonationToken
      ? { impersonation: true, user: { id: `impersonation:${p.id}`, email: p.email, user_metadata: {} } }
      : session,
  }
}

async function loadPatientProfileSnapshot() {
  const authResult = await withTimeout(
    supabase.auth.getSession(),
    1500,
    'Patient auth session took too long to load'
  ).catch(() => ({ data: {} }))
  const session = authResult?.data?.session || null
  const impersonationToken = getImpersonationToken()
  if (!session && !impersonationToken) {
    return { loading: false, patient: null, org: null, session: null }
  }

  const key = patientProfileCacheKey(session, impersonationToken)
  if (cachedPatientProfile && cachedPatientProfileKey === key && cachedPatientProfileExpiresAt > Date.now()) {
    return cachedPatientProfile
  }
  if (patientProfilePromise && patientProfilePromiseKey === key) {
    return patientProfilePromise
  }

  patientProfilePromiseKey = key
  patientProfilePromise = (async () => {
    try {
      const me = await apiGet('/api/mso/me')
      return msoProfileState(me, session, impersonationToken)
    } catch (msoErr) {
      if (impersonationToken) clearImpersonationToken()
      if (isAuthFailure(msoErr)) throw msoErr
      console.warn('[usePatient] /api/mso/me failed, trying legacy portal profile', msoErr)
    }

    const me = await apiGet('/api/portal/me')
    return { loading: false, patient: me.patient, org: me.org, session }
  })()

  try {
    const snapshot = await patientProfilePromise
    cachedPatientProfile = snapshot
    cachedPatientProfileKey = key
    cachedPatientProfileExpiresAt = Date.now() + PATIENT_PROFILE_CACHE_MS
    return snapshot
  } finally {
    patientProfilePromise = null
    patientProfilePromiseKey = ''
  }
}

export function usePatient() {
  const [state, setState] = useState({ loading: true, patient: null, org: null, session: null, sourceUnavailable: false, error: '' })

  useEffect(() => {
    let mounted = true
    const unauthenticated = () => {
      if (mounted) setState({ loading: false, patient: null, org: null, session: null, sourceUnavailable: false, error: '' })
    }

    async function load() {
      let currentSession = null
      try {
        const { data } = await withTimeout(
          supabase.auth.getSession(),
          1500,
          'Patient auth session took too long to load'
        ).catch(() => ({ data: {} }))
        currentSession = data?.session || null
        const snapshot = await withTimeout(
          loadPatientProfileSnapshot(),
          PATIENT_PROFILE_LOAD_TIMEOUT_MS,
          'Patient profile took too long to load'
        )
        if (mounted) setState(snapshot)
      } catch (err) {
        console.error('[usePatient] /api/portal/me failed', err)
        clearPatientProfileCache()
        if (isSourceUnavailable(err) && currentSession) {
          if (mounted) {
            setState({
              loading: false,
              patient: null,
              org: null,
              session: currentSession,
              sourceUnavailable: true,
              error: err.message || 'KIS Rx patient portal data is temporarily unavailable.',
            })
          }
          return
        }
        if (isAuthFailure(err)) {
          await withTimeout(
            supabase.auth.signOut().catch(() => {}),
            5000,
            'Patient sign out took too long'
          ).catch(() => {})
        }
        unauthenticated()
      }
    }
    const refresh = () => {
      clearPatientProfileCache()
      load()
    }
    load()
    window.addEventListener('kis:patient:refresh', refresh)
    const sub = supabase.auth.onAuthStateChange((_e, sess) => {
      clearPatientProfileCache()
      if (!sess && !getImpersonationToken()) setState(s => ({ ...s, session: null, patient: null, sourceUnavailable: false, error: '' }))
    })
    return () => {
      mounted = false
      window.removeEventListener('kis:patient:refresh', refresh)
      sub.data.subscription.unsubscribe()
    }
  }, [])

  return state
}
